import { useState } from 'react'

import { СУТКИ_ЗЕМЛИ_Ч, ru, rotatingEarth } from '../../lib/physics'
import { Choice, Predict, Slider, useFrames } from './common'

/**
 * Вращающаяся Земля (урок 33): вращение сплющивает Землю у полюсов, поэтому на полюсе
 * тело ближе к центру и g больше, чем на экваторе. Модель упрощённая: эффект растёт как
 * квадрат скорости вращения; при настоящих сутках — настоящие числа.
 */

const R = 100

export default function EarthLab() {
  const [сутки, setСутки] = useState(СУТКИ_ЗЕМЛИ_Ч)
  const [увеличить, setУвеличить] = useState(20)
  const [фаза, setФаза] = useState(0)

  const з = rotatingEarth(сутки)
  // на рисунке сплющенность можно увеличить: при настоящих сутках она — треть процента
  const сжатиеРис = Math.min(з.сжатие * увеличить, 0.45)
  const rx = R * (1 + сжатиеРис / 3)
  const ry = R * (1 - (2 * сжатиеРис) / 3)

  useFrames((dt) => setФаза((p) => (p + (dt * 2 * Math.PI * 1.5) / (сутки / 4)) % (2 * Math.PI)))

  const меридианы = [0, 1, 2, 3, 4, 5].map((i) => {
    const a = фаза + (i * Math.PI) / 6
    return Math.cos(a) * rx
  })

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 260" role="img" aria-label="Вращающаяся Земля">
        <line x1={180} y1={10} x2={180} y2={250} className="ink-muted" strokeDasharray="4 4" />
        <ellipse cx={180} cy={130} rx={rx} ry={ry} className="fill-earth ink" strokeWidth={1.5} />
        {меридианы.map((mx, i) => (
          <ellipse key={i} cx={180} cy={130} rx={Math.abs(mx)} ry={ry} className="ink-muted" fill="none" strokeWidth={0.8} />
        ))}
        <line x1={180 - rx} y1={130} x2={180 + rx} y2={130} className="ink-muted" strokeWidth={0.8} />
        {/* наблюдатели с гирями на полюсе и на экваторе */}
        <circle cx={180} cy={130 - ry - 6} r={5} className="fill-heavy" />
        <text x={190} y={130 - ry - 6} className="lab-label">полюс: g = {ru(з.gПолюс, 2)}</text>
        <circle cx={180 + rx + 6} cy={130} r={5} className="fill-heavy" />
        <text x={180 + rx - 4} y={118} textAnchor="end" className="lab-label">экватор: g = {ru(з.gЭкватор, 2)}</text>
        <text x={180} y={252} textAnchor="middle" className="lab-tick">ось вращения</text>
      </svg>

      <div className="lab-readout">
        <span>сутки: {сутки} ч</span>
        <span>экваториальный радиус больше полярного на {ru(з.разностьКм, 0)} км</span>
        <span>
          g на полюсе {ru(з.gПолюс, 2)}, на экваторе {ru(з.gЭкватор, 2)} м/с²
        </span>
      </div>
      <div className="lab-caption">
        На полюсе тело ближе к центру Земли и притягивается сильнее; на экваторе вдобавок
        «мешает» вращение. Чем быстрее вращение, тем сильнее сплющивание и разница g.
      </div>

      <Predict
        question="Где гиря на пружинных весах покажет больше — на полюсе или на экваторе?"
        options={['на полюсе', 'на экваторе', 'одинаково']}
        right={0}
        revealed
        explain="На полюсе g больше (9,83 против 9,78 м/с²) — разница небольшая, около половины процента."
      />

      <Slider label="Продолжительность суток" value={сутки} min={4} max={24} step={1} unit="ч" onChange={setСутки} />
      <div className="lab-sub">Сплющенность на рисунке</div>
      <Choice
        options={[
          { value: 1, label: 'как есть' },
          { value: 20, label: 'увеличить ×20' },
        ]}
        value={увеличить}
        onChange={setУвеличить}
      />
      <p className="muted small">
        Модель упрощённая: при 24-часовых сутках числа настоящие, при более быстром вращении —
        оценка. Сплющенность настоящей Земли — треть процента, на глаз её не видно.
      </p>
    </div>
  )
}
